import { useMemo } from 'react';

import type { IdSalesUnitDto, SalesUnitDto } from '@/services/ws-companies/src/main/resources/rest-api';
import { getCompanySalesUnits } from '@/services/ws-companies/src/main/resources/rest-api';
import { useSubscribe } from '@/js/libs/observable';
import { useRequest } from '@/js/libs/use-request';

import type { Option } from 'src/types';

const salesUnitToOption = ({ id, isActive, isDefault, name }: SalesUnitDto): Option<IdSalesUnitDto> => ({
  label: name,
  value: id,
  active: isActive,
  marked: isDefault,
});

export const useSalesUnitOptions = () => {
  const salesUnitsRequest = useRequest({
    apiMethod: getCompanySalesUnits,
    params: {},
  });

  const salesUnits = useSubscribe(salesUnitsRequest.data$, (r) => r?.data ?? []);
  const salesUnitsLoading = useSubscribe(salesUnitsRequest.pending$);

  const salesUnitOptions = useMemo(() => salesUnits.map(salesUnitToOption), [salesUnits]);
  const defaultSalesUnit = useMemo(() => {
    const defaultItem = salesUnits.find((item) => item.isDefault);

    return defaultItem ? salesUnitToOption(defaultItem) : null;
  }, [salesUnits]);

  return useMemo(
    () => ({
      salesUnits,
      salesUnitsLoading,
      salesUnitOptions,
      defaultSalesUnit,
    }),
    [salesUnits, salesUnitOptions, defaultSalesUnit, salesUnitsLoading],
  );
};
